/*
 * File: ApplicationService.ts
 * Project: receiving-rules
 * -----
 * Last Modified: 01 Oct 2020 4:59:27 pm
 * -----
 * Copyright (c) 2020 Elegant Software Solutions All rights reserved.
 */

// import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { HTTPStatusCodes } from 'common/enums/HTTPStatusCodes';
import { PackingSlipModel } from 'models/PackingSlipModel';
import { POLineNumberQueryModel } from 'models/POLineNumberQueryModel';
import { POReceiptInputModel } from 'models/POReceiptInputModel';
import { POReceiptModel } from 'models/POReceiptModel';
import { POReceiptResponseModel } from 'models/POReceiptResponseModel';
import { DTOModel } from '../models/DTOModel';

const grannyBirdBaseUrl = process.env.REACT_APP_GRANNY_BIRD_BASE_URL;
const grannyBirdfunctionsKey = process.env.REACT_APP_GRANNY_BIRD_FUNCTION_KEY;

const getPOReceipts = async (token: string, dataAreaId: string, offset: number, options: POReceiptInputModel): Promise<DTOModel<POReceiptResponseModel | null>> => {
  const dateFilter = options.startDate.length && options.endDate.length ? 'DateRange' : '';

  const axiosOptions: AxiosRequestConfig = {
    headers: {
      authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-functions-key': grannyBirdfunctionsKey
    },
    method: 'get',
    url: `${grannyBirdBaseUrl}/dataAreaId/${dataAreaId}/purchaseOrders?offset=${offset}&purchaseOrderNumberContains=${options.mdsiPONumber}&customerOrderNumberContains=${options.custPONumber}&vendorAccountContains=${options.vendorAccount}&vendorNameContains=${options.vendorName}&warehouseContains=${options.warehouse}&startDate=${options.startDate}&endDate=${options.endDate}&dateFilter=${dateFilter}`
  };

  const response: DTOModel<POReceiptResponseModel | null> = {
    data: null,
    error: null,
    status: null
  };

  try {
    const resp: AxiosResponse<POReceiptResponseModel | null> = await axios(axiosOptions);

    response.data = resp.data;
    response.status = resp.status;
  } catch (err) {
    const axiosError = err as AxiosError;

    response.status = (axiosError.response?.status as HTTPStatusCodes) ?? null;
    response.error = {
      message: axiosError.response?.data ?? 'Unable to get error message from server.',
      requestURL: axiosOptions.url as string,
      requestParams: { applicationId: process.env.REACT_APP_APPLICATION_ID }
    };
  }

  return response;
};

const getPOLineNumber = async (token: string, dataAreaId: string, options: POLineNumberQueryModel): Promise<DTOModel<POReceiptModel | null>> => {
  const axiosOptions: AxiosRequestConfig = {
    headers: {
      authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-functions-key': grannyBirdfunctionsKey
    },
    method: 'get',
    url: `${grannyBirdBaseUrl}/dataAreaId/${dataAreaId}/purchaseOrders/${options.MDSIPONumber}/lineNumbers`
  };

  const response: DTOModel<POReceiptModel | null> = {
    data: null,
    error: null,
    status: null
  };

  try {
    const resp: AxiosResponse<POReceiptModel | null> = await axios(axiosOptions);

    response.data = resp.data;
    response.status = resp.status;
  } catch (err) {
    const axiosError = err as AxiosError;

    response.status = (axiosError.response?.status as HTTPStatusCodes) ?? null;
    response.error = {
      message: axiosError.response?.data ?? 'Unable to get error message from server.',
      requestURL: axiosOptions.url as string,
      requestParams: { applicationId: process.env.REACT_APP_APPLICATION_ID }
    };
  }

  return response;
};

const submitPackingSlip = async (token: string, dataAreaId: string, options: POLineNumberQueryModel, data: PackingSlipModel): Promise<DTOModel<POReceiptModel | null>> => {
  const axiosOptions: AxiosRequestConfig = {
    headers: {
      authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-functions-key': grannyBirdfunctionsKey
    },
    method: 'post',
    url: `${grannyBirdBaseUrl}/${dataAreaId}/purchaseOrders/${options.MDSIPONumber}/productReceipts`,
    data: data
  };

  const response: DTOModel<POReceiptModel | null> = {
    data: null,
    error: null,
    status: null
  };

  try {
    const resp: AxiosResponse<POReceiptModel | null> = await axios(axiosOptions);

    response.data = resp.data;
    response.status = resp.status;
  } catch (err) {
    const axiosError = err as AxiosError;

    response.status = (axiosError.response?.status as HTTPStatusCodes) ?? null;
    response.error = {
      message: axiosError.response?.data ?? 'Unable to get error message from server.',
      requestURL: axiosOptions.url as string,
      requestParams: { applicationId: process.env.REACT_APP_APPLICATION_ID }
    };
  }

  return response;
};

export const POReceiptService = {
  getPOReceipts,
  getPOLineNumber,
  submitPackingSlip
};
